import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PRESENCE_OPERATORS, } from '@super/shared-types';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { OPERATOR_LABELS, OPERATOR_SYMBOLS, entryOf, firstValue, pickedFromList, rowForEntry, storedValue, } from '../../equation-options';
import { FieldHelp } from '../FieldHelp';
const SELECT = 'w-full h-8 rounded-md border bg-background px-2 text-xs disabled:opacity-60';
const GROUPS = [
    'System variables',
    'Contact variables',
    'Custom variables',
    'Events',
    'Source node outputs',
];
/** "Take this path if all / any of the following conditions match." */
export function EquationPanel({ condition, entries, whenEmpty, onChange, }) {
    const rows = condition?.rows ?? [];
    const mode = condition?.mode ?? 'all';
    // A condition the author is adding has nothing picked yet, so it is held
    // here rather than in the equation.
    const [adding, setAdding] = useState(rows.length === 0);
    const set = (next, nextMode = mode) => onChange({ type: 'equation', mode: nextMode, rows: next });
    return (_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "space-y-1", children: [_jsx(Label, { htmlFor: "equation-mode", className: "text-xs", children: "Take this path if" }), _jsxs("div", { className: "flex items-center gap-2 text-xs", children: [_jsxs("select", { id: "equation-mode", className: "h-8 rounded-md border bg-background px-2 text-xs", value: mode, onChange: e => set(rows, e.target.value), children: [_jsx("option", { value: "all", children: "All" }), _jsx("option", { value: "any", children: "Any" })] }), _jsx("span", { className: "text-muted-foreground", children: "of the following conditions match" })] })] }), rows.map((row, index) => (_jsx(ConditionRow, { row: row, entries: entries, onChange: next => set(rows.map((r, i) => (i === index ? next : r))), onRemove: () => {
                    const next = rows.filter((_, i) => i !== index);
                    set(next);
                    if (next.length === 0)
                        setAdding(true);
                } }, index))), adding && (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(EntryPicker, { value: "", entries: entries, autoFocus: rows.length > 0, onPick: row => {
                            set([...rows, row]);
                            setAdding(false);
                        } }), rows.length > 0 && _jsx(RemoveButton, { onClick: () => setAdding(false) })] })), _jsxs(Button, { variant: "outline", size: "sm", className: "w-full h-8 text-xs border-dashed", disabled: adding, onClick: () => setAdding(true), children: [_jsx(Plus, { className: "h-3.5 w-3.5 mr-1" }), "Add condition"] }), rows.length === 0 && (_jsx(FieldHelp, { children: whenEmpty ??
                    'Pick a variable to compare, an event, or an output of the node this arrow leaves.' }))] }));
}
function ConditionRow({ row, entries, onChange, onRemove, }) {
    const found = entryOf(row, entries);
    if (!found) {
        return (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("p", { className: "flex-1 text-xs text-amber-700 dark:text-amber-500", children: "This condition reads something the graph no longer has." }), _jsx(RemoveButton, { onClick: onRemove })] }));
    }
    const { entry, state } = found;
    const value = entry.value;
    const valueRow = row.kind === 'value' ? row : undefined;
    const truth = value?.type === 'boolean' && entry.states.length === 0;
    return (_jsxs("div", { className: "space-y-1", children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("div", { className: "min-w-0 flex-[3]", children: _jsx(EntryPicker, { value: entry.key, entries: entries, onPick: onChange }) }), value && truth && (_jsx("div", { className: "min-w-0 flex-[2]", children: _jsx(TruthPicker, { value: value, row: valueRow, onChange: onChange }) })), !truth && (value || entry.states.length > 1) && (_jsx(CheckPicker, { entry: entry, value: value, state: state, row: valueRow, onChange: onChange })), value &&
                        !truth &&
                        valueRow &&
                        !PRESENCE_OPERATORS.includes(valueRow.op) && (_jsx("div", { className: "min-w-0 flex-[2]", children: _jsx(ValueInput, { value: value, current: valueRow.value, onChange: next => onChange(next === undefined
                                ? { kind: 'value', ref: valueRow.ref, op: valueRow.op }
                                : { ...valueRow, value: next }) }) })), _jsx(RemoveButton, { onClick: onRemove })] }), (state?.description || entry.description) && (_jsx(FieldHelp, { children: state?.description || entry.description })), value?.maybeMissing && valueRow && (_jsx(FieldHelp, { children: "May be missing: only some ways to this arrow ask for it." }))] }));
}
/**
 * How a value is checked: an operator, shown as its symbol once picked, or,
 * for an output of the node the arrow leaves, how it went.
 */
function CheckPicker({ entry, value, state, row, onChange, }) {
    const operators = value?.operators ?? [];
    const current = state ? `state:${state.key}` : `op:${row?.op}`;
    const shown = state ? state.label : row ? OPERATOR_SYMBOLS[row.op] : '';
    const pick = (picked) => {
        if (picked.startsWith('state:')) {
            const next = entry.states.find(s => `state:${s.key}` === picked);
            if (next)
                onChange(next.row);
            return;
        }
        if (!value)
            return;
        const op = picked.slice('op:'.length);
        const kept = row && !PRESENCE_OPERATORS.includes(row.op)
            ? row.value
            : firstValue(value);
        onChange(PRESENCE_OPERATORS.includes(op)
            ? { kind: 'value', ref: value.ref, op }
            : { kind: 'value', ref: value.ref, op, value: kept });
    };
    if (entry.states.length + operators.length < 2) {
        return (_jsx("span", { className: "shrink-0 px-1 text-xs text-muted-foreground", children: shown }));
    }
    return (_jsxs(ShownSelect, { label: "Check", shown: shown, className: state ? 'min-w-0 flex-[2]' : 'w-14 shrink-0', value: current, onChange: pick, children: [entry.states.length > 0 && (_jsx("optgroup", { label: "How it went", children: entry.states.map(s => (_jsx("option", { value: `state:${s.key}`, children: s.label }, s.key))) })), operators.length > 0 && (_jsx("optgroup", { label: entry.states.length > 0 ? 'Its value' : 'Compare', children: operators.map(op => (_jsx("option", { value: `op:${op}`, children: OPERATOR_LABELS[op] }, op))) }))] }));
}
/**
 * A dropdown that shows a short form of what is picked, such as an
 * operator's symbol, and the full wording when open.
 */
function ShownSelect({ label, shown, className, value, autoFocus, onChange, children, }) {
    return (_jsxs("div", { className: `relative ${className ?? ''}`, children: [_jsx("select", { "aria-label": label, autoFocus: autoFocus, className: "peer absolute inset-0 h-full w-full cursor-pointer opacity-0", value: value, onChange: e => onChange(e.target.value), children: children }), _jsxs("div", { "aria-hidden": true, className: "pointer-events-none flex h-8 items-center justify-between gap-1 rounded-md border bg-background px-2 text-xs peer-focus-visible:ring-2 peer-focus-visible:ring-ring", children: [_jsx("span", { className: "truncate", children: shown }), _jsx(ChevronDown, { className: "h-3 w-3 shrink-0 opacity-60" })] })] }));
}
/** A true or false variable in one dropdown: its value, or whether it was heard. */
function TruthPicker({ value, row, onChange, }) {
    const options = [
        ...value.choices.map(choice => ({
            key: `equals:${choice.value}`,
            label: choice.label,
        })),
        ...(value.operators.includes('exists')
            ? [{ key: 'exists', label: 'heard' }]
            : []),
        ...(value.operators.includes('not_exists')
            ? [{ key: 'not_exists', label: 'not heard yet' }]
            : []),
    ];
    const current = !row
        ? ''
        : row.op === 'equals'
            ? `equals:${String(row.value)}`
            : row.op;
    const pick = (key) => {
        if (key === 'exists' || key === 'not_exists') {
            onChange({ kind: 'value', ref: value.ref, op: key });
            return;
        }
        onChange({
            kind: 'value',
            ref: value.ref,
            op: 'equals',
            value: storedValue(value.type, key.slice('equals:'.length)),
        });
    };
    return (_jsx(ShownSelect, { label: "Value", shown: options.find(o => o.key === current)?.label ?? '', value: current, onChange: pick, children: options.map(option => (_jsx("option", { value: option.key, children: option.label }, option.key))) }));
}
/** The first dropdown of a condition: which variable, event or output it checks. */
function EntryPicker({ value, entries, autoFocus, onPick, }) {
    const picked = entries.find(e => e.key === value);
    return (_jsxs(ShownSelect, { label: "What to check", shown: picked?.label ?? '(variable)', autoFocus: autoFocus, value: value, onChange: key => {
            const entry = entries.find(x => x.key === key);
            const row = entry && rowForEntry(entry);
            if (row)
                onPick(row);
        }, children: [value === '' && (_jsx("option", { value: "", disabled: true, children: "(variable)" })), GROUPS.map(group => {
                const inGroup = entries.filter(e => e.group === group && (!e.hidden || e.key === value));
                if (inGroup.length === 0)
                    return null;
                return (_jsx("optgroup", { label: group, children: inGroup.map(e => (_jsx("option", { value: e.key, hidden: e.hidden, children: e.preview ? `${e.label}: ${truncate(e.preview, 40)}` : e.label }, e.key))) }, group));
            })] }));
}
/** The value a condition compares with. Empty means no value yet. */
function ValueInput({ value, current, onChange, }) {
    const shown = current === undefined ? '' : String(current);
    const [draft, setDraft] = useState(shown);
    useEffect(() => setDraft(shown), [shown]);
    if (pickedFromList(value.type)) {
        const known = value.choices.some(c => c.value === shown);
        return (_jsxs("select", { "aria-label": "Value", className: SELECT, value: known ? shown : '', disabled: value.choices.length === 0, onChange: e => onChange(storedValue(value.type, e.target.value)), children: [!known && (_jsx("option", { value: "", disabled: true, children: value.emptyNote ?? (shown || 'Value') })), value.choices.map(choice => (_jsx("option", { value: choice.value, children: choice.label }, choice.value)))] }));
    }
    if (value.type === 'integer') {
        return (_jsx(Input, { "aria-label": "Value", inputMode: "numeric", placeholder: "Whole number", className: "text-xs h-8", value: draft, onChange: e => {
                const text = e.target.value;
                setDraft(text);
                onChange(/^-?\d+$/.test(text.trim()) ? Number(text.trim()) : undefined);
            } }));
    }
    return (_jsx(Input, { "aria-label": "Value", type: value.type === 'date' ? 'date' : 'text', placeholder: "Value", className: "text-xs h-8", value: shown, onChange: e => onChange(e.target.value === '' ? undefined : e.target.value) }));
}
function RemoveButton({ onClick }) {
    return (_jsx(Button, { variant: "ghost", size: "icon", className: "h-7 w-7 shrink-0", "aria-label": "Remove this condition", onClick: onClick, children: _jsx(Trash2, { className: "h-3.5 w-3.5" }) }));
}
function truncate(text, max) {
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}
//# sourceMappingURL=EquationPanel.js.map